// Requests to the Quanty One API, on the same origin as the app (implementation.md section 6).
// Every POST goes through `post`, which adds the two headers the API needs: `x-quanty`, which a
// cross-site form cannot send, and `x-amz-content-sha256`, the hex SHA-256 of the exact body bytes,
// without which CloudFront's origin access control does not sign the body and the request is refused.

export interface ApiAnswer<T = Record<string, unknown>> {
  status: number;
  data: T;
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function post<T = Record<string, unknown>>(path: string, body?: unknown, headers: Record<string, string> = {}): Promise<ApiAnswer<T>> {
  const text = body === undefined ? "" : JSON.stringify(body);
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      ...headers,
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      "x-quanty": "1",
      "x-amz-content-sha256": await sha256Hex(text),
    },
    body: text,
  });
  return { status: res.status, data: await jsonOf<T>(res) };
}

export async function get<T = Record<string, unknown>>(path: string): Promise<ApiAnswer<T>> {
  const res = await fetch(path, { credentials: "same-origin" });
  return { status: res.status, data: await jsonOf<T>(res) };
}

async function jsonOf<T>(res: Response): Promise<T> {
  try {
    return (await res.json()) as T;
  } catch {
    return {} as T;
  }
}
